import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { config } from '../../config';
import { Container, Row, Col, Card, Button, Form, Badge, Accordion, Modal, Alert, Table } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import SweetLayout from '../../Layout/SweetLayout';
import Select from 'react-select';
import { AppContext } from '../../AppContext/Context';
import './AiGuidance.css';

const ACCOUNT_CACHE_KEY = 'admin_accounts_cache';

// Types
interface Account {
  account_id: number;
  name: string;
  owner_user_id: string;
}

interface Channel {
  channel: number;
  name: string;
}

interface LLMAssessment {
  model: string;
  output_format: string;
  system_prompt: string;
  options_format?: string;
  topic_text_samples?: string[];
  description?: string;
  options?: string[] | Record<string, string>[];
}

interface TriggerCondition {
  score_threshold?: number;
  float_comparator?: string;
  bool_value?: boolean;
}

interface PromptAddition {
  priority: number;
  prompt_to_add: string;
  include_if_lower_priority_p: boolean;
}

interface MCPConfig {
  type: string;
  server_url: string;
  server_label: string;
  require_approval: string;
  server_description: string;
}

interface WorkflowAction {
  trigger_condition: TriggerCondition;
  action_type: string;
  prompt_addition?: PromptAddition;
  custom_system_prompt?: string;
  immediate_response?: string;
  retrieval_adjustment_prompt?: string;
  answer_adjustment_prompt?: string;
  use_retrievals_N?: number;
  tag_value?: string;
  mcp?: MCPConfig;
  tool?: any;
}

interface AssessmentSpec {
  assessment_method: string;
  llm_assessment?: LLMAssessment;
  workflow_stage?: string;
}

interface UUA {
  title: string;
  assessment_spec: AssessmentSpec;
  workflow_actions: WorkflowAction[];
  channels?: number[];
  account_id?: number;
}

interface UUAListItem {
  id: string;
  title: string;
  account_id: number;
  channels: number[];
  created_at: string;
  updated_at: string;
  content: UUA;
}

const normalizeAccount = (account: any): Account | null => {
  if (!account || typeof account !== 'object') {
    return null;
  }
  return {
    account_id: account.account_id ?? account.id,
    name: account.name ?? account.account_name ?? '',
    owner_user_id: account.owner_user_id ?? account.owner ?? account.account_owner ?? '',
  };
};

const readAccountsFromCache = (): Account[] | null => {
  try {
    const cached = sessionStorage.getItem(ACCOUNT_CACHE_KEY);
    if (!cached) {
      return null;
    }
    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) ? parsed.map(normalizeAccount).filter(Boolean) as Account[] : null;
  } catch (error) {
    console.warn('Failed to read accounts from cache', error);
    return null;
  }
};

const writeAccountsToCache = (accounts: Account[]) => {
  try {
    const normalized = Array.isArray(accounts)
      ? accounts.map(normalizeAccount).filter(Boolean)
      : [];
    sessionStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify(normalized));
  } catch (error) {
    console.warn('Failed to write accounts to cache', error);
  }
};

const AiGuidance: React.FC = () => {
  const { user, getIdTokenClaims } = useAuth0();
  const { setSelectedAccount: setAppContextAccount } = useContext(AppContext);

  // Account state
  const [accounts, setAccounts] = useState<Account[]>(() => {
    const cached = readAccountsFromCache();
    return cached || [];
  });
  const [selectedAccount, setSelectedAccount] = useState<{ value: number; label: string } | null>(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const hasFetchedAccounts = useRef(false);

  // Channel state
  const [channels, setChannels] = useState<{ value: number; label: string }[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<{ value: number; label: string }[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);

  // UUA list state
  const [uuaList, setUuaList] = useState<UUAListItem[]>([]);
  const [loadingUuas, setLoadingUuas] = useState(false);
  const [selectedUua, setSelectedUua] = useState<UUAListItem | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<any>({
    title: '',
    assessment_type: '',
    assessment_method: 'llm_assessment',
    workflow_stage: 'pre_generator',
    model: 'openai-gpt-4o-mini',
    system_prompt: '',
    topic_text_samples: [''],
    description: '',
    options: [''],
    workflow_actions: [],
  });
  const [submitting, setSubmitting] = useState(false);

  // Modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [uuaToDelete, setUuaToDelete] = useState<UUAListItem | null>(null);

  const fetchAccounts = useCallback(async () => {
    const cached = readAccountsFromCache();
    if (cached && cached.length > 0) {
      setAccounts(cached);
      hasFetchedAccounts.current = true;
      return;
    }

    try {
      setLoadingAccounts(true);
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const options = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      };
      const path = `/account`;
      const url = `${config.baseURL}/admin/v1${path}`;
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.json();
      const normalized = Array.isArray(result)
        ? result.map(normalizeAccount).filter(Boolean) as Account[]
        : [];
      setAccounts(normalized);
      writeAccountsToCache(normalized);
      hasFetchedAccounts.current = true;
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to load accounts');
    } finally {
      setLoadingAccounts(false);
    }
  }, [getIdTokenClaims]);

  useEffect(() => {
    if (user && !hasFetchedAccounts.current && !loadingAccounts) {
      if (accounts.length > 0) {
        hasFetchedAccounts.current = true;
        return;
      }
      fetchAccounts();
    }
  }, [user, loadingAccounts, accounts.length, fetchAccounts]);

  const fetchChannels = useCallback(async () => {
    if (!selectedAccount || !selectedAccount.value) {
      setChannels([]);
      return;
    }

    try {
      setLoadingChannels(true);
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const options = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      };
      const path = `/channel_list?account_id=${selectedAccount.value}`;
      const url = `${config.baseURL}/admin/v1${path}`;
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.json();
      const channelOptions = Array.isArray(result)
        ? result.map((ch: Channel) => ({
            value: ch.channel,
            label: `${ch.channel} - ${ch.name}`,
          }))
        : [];
      setChannels(channelOptions);
    } catch (error) {
      console.error('Error fetching channels:', error);
      toast.error('Failed to load channels');
    } finally {
      setLoadingChannels(false);
    }
  }, [getIdTokenClaims, selectedAccount]);

  useEffect(() => {
    if (user && selectedAccount) {
      fetchChannels();
    } else {
      setChannels([]);
      setSelectedChannels([]);
    }
  }, [user, selectedAccount, fetchChannels]);

  const fetchUuas = useCallback(async () => {
    if (!selectedAccount) return;

    try {
      setLoadingUuas(true);
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const url = `${config.baseURL}/admin/v1/uua?account_id=${selectedAccount.value}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      setUuaList(result.uuas || []);
    } catch (error) {
      console.error('Error fetching UUAs:', error);
      toast.error('Failed to load AI Guidance configurations');
    } finally {
      setLoadingUuas(false);
    }
  }, [getIdTokenClaims, selectedAccount]);

  useEffect(() => {
    if (selectedAccount) {
      fetchUuas();
    }
  }, [selectedAccount, fetchUuas]);

  const handleAccountChange = (selectedOption: { value: number; label: string } | null) => {
    setSelectedAccount(selectedOption);
    if (selectedOption && selectedOption.value) {
      const account = accounts.find(acc => acc.account_id === selectedOption.value);
      if (account) {
        setAppContextAccount(account);
      }
    }
    setShowForm(false);
    setEditMode(false);
    resetForm();
    setSelectedChannels([]);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      assessment_type: '',
      assessment_method: 'llm_assessment',
      workflow_stage: 'pre_generator',
      model: 'openai-gpt-4o-mini',
      system_prompt: '',
      topic_text_samples: [''],
      description: '',
      options: [''],
      workflow_actions: [],
    });
    setSelectedChannels([]);
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addArrayField = (field: string) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: [...prev[field], ''],
    }));
  };

  const removeArrayField = (field: string, index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: prev[field].filter((_: any, i: number) => i !== index),
    }));
  };

  const updateArrayField = (field: string, index: number, value: string) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: prev[field].map((item: string, i: number) => (i === index ? value : item)),
    }));
  };

  const addWorkflowAction = () => {
    const newAction: WorkflowAction = {
      trigger_condition: {
        score_threshold: 0.5,
        float_comparator: '>=',
      },
      action_type: 'prompt_addition',
      prompt_addition: {
        priority: 1,
        prompt_to_add: '',
        include_if_lower_priority_p: true,
      },
    };
    setFormData((prev: any) => ({
      ...prev,
      workflow_actions: [...prev.workflow_actions, newAction],
    }));
  };

  const removeWorkflowAction = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      workflow_actions: prev.workflow_actions.filter((_: any, i: number) => i !== index),
    }));
  };

  const updateWorkflowAction = (index: number, field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      workflow_actions: prev.workflow_actions.map((action: WorkflowAction, i: number) =>
        i === index ? { ...action, [field]: value } : action
      ),
    }));
  };

  const buildUuaPayload = (): UUA => {
    const { assessment_type, assessment_method, workflow_stage, model, system_prompt } = formData;

    const llm_assessment: LLMAssessment = {
      model,
      output_format: '{"reason": str <reason>, "score": float <score>}',
      system_prompt: system_prompt || 'default_topic_match_prompt',
      options_format: 'list[str]',
    };

    // Add type-specific fields
    if (assessment_type === 'topic_') {
      llm_assessment.topic_text_samples = formData.topic_text_samples.filter((s: string) => s.trim());
    } else if (assessment_type === 'intent_') {
      llm_assessment.description = formData.description;
    } else if (assessment_type === 'characterization_' || assessment_type === 'select_') {
      llm_assessment.options = formData.options.filter((s: string) => s.trim());
    }

    const assessment_spec: AssessmentSpec = {
      assessment_method,
      llm_assessment,
      workflow_stage,
    };

    return {
      title: formData.title,
      assessment_spec,
      workflow_actions: formData.workflow_actions,
      channels: selectedChannels.map(ch => ch.value),
      account_id: selectedAccount?.value,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.assessment_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.workflow_actions.length === 0) {
      toast.error('Please add at least one workflow action');
      return;
    }

    try {
      setSubmitting(true);
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;

      const payload = buildUuaPayload();

      const url = editMode && selectedUua
        ? `${config.baseURL}/admin/v1/uua/${selectedUua.id}`
        : `${config.baseURL}/admin/v1/uua`;

      const response = await fetch(url, {
        method: editMode ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
      }

      toast.success(`AI Guidance ${editMode ? 'updated' : 'created'} successfully`);
      setShowForm(false);
      setEditMode(false);
      resetForm();
      fetchUuas();
    } catch (error: any) {
      console.error('Error saving AI Guidance:', error);
      toast.error(error.message || 'Failed to save AI Guidance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (uua: UUAListItem) => {
    setSelectedUua(uua);
    setEditMode(true);
    setShowForm(true);

    // Parse UUA content and populate form
    const content = uua.content;
    const assessment = content.assessment_spec.llm_assessment;

    setFormData({
      title: content.title,
      assessment_type: getAssessmentType(content.title),
      assessment_method: content.assessment_spec.assessment_method,
      workflow_stage: content.assessment_spec.workflow_stage || 'pre_generator',
      model: assessment?.model || 'openai-gpt-4o-mini',
      system_prompt: assessment?.system_prompt || '',
      topic_text_samples: assessment?.topic_text_samples || [''],
      description: assessment?.description || '',
      options: assessment?.options || [''],
      workflow_actions: content.workflow_actions || [],
    });

    setSelectedChannels(
      channels.filter(ch => content.channels?.includes(ch.value))
    );
  };

  const getAssessmentType = (title: string): string => {
    if (title.startsWith('topic_')) return 'topic_';
    if (title.startsWith('characterization_')) return 'characterization_';
    if (title.startsWith('intent_')) return 'intent_';
    if (title.startsWith('select_')) return 'select_';
    if (title.startsWith('channel_')) return 'channel_';
    return '';
  };

  const handleDelete = async () => {
    if (!uuaToDelete) return;

    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;

      const response = await fetch(`${config.baseURL}/admin/v1/uua/${uuaToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      toast.success('AI Guidance deleted successfully');
      setShowDeleteModal(false);
      setUuaToDelete(null);
      fetchUuas();
    } catch (error) {
      console.error('Error deleting UUA:', error);
      toast.error('Failed to delete AI Guidance');
    }
  };

  const handleView = (uua: UUAListItem) => {
    setSelectedUua(uua);
    setShowViewModal(true);
  };

  // Show account selection screen if no account is selected
  if (!selectedAccount) {
    return (
      <SweetLayout>
        <div className="page-content">
          <Container fluid="xxl">
            <div style={{ marginTop: '40px' }}>
              <Row>
                <Col md={8} lg={6} className="mx-auto">
                  <div className="ai-guidance-select-card">
                    <h2 className="ai-guidance-select-title">AI Guidance</h2>
                    <p className="ai-guidance-select-subtitle">
                      Select an account to manage AI guidance configurations (UUA).
                    </p>

                    <div style={{ marginBottom: '16px' }}>
                      <label className="form-label-custom">Account</label>
                      <Select
                        id="select-account"
                        onChange={handleAccountChange}
                        value={selectedAccount}
                        options={(accounts || []).map((account) => ({
                          value: account.account_id,
                          label: `${account.account_id} - ${account.name} - ${account.owner_user_id}`,
                        }))}
                        isSearchable
                        placeholder="Select an account..."
                        isLoading={loadingAccounts}
                      />
                    </div>

                    {loadingAccounts && (
                      <div className="text-center py-3 text-muted">Loading accounts...</div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Container>
        </div>
        <ToastContainer />
      </SweetLayout>
    );
  }

  // Show main content once account is selected
  return (
    <SweetLayout>
      <Container fluid>
        <div className="ai-guidance-account-selector">
          <label className="form-label-custom">Account</label>
          <Select
            id="select-account"
            onChange={handleAccountChange}
            value={selectedAccount}
            options={(accounts || []).map((account) => ({
              value: account.account_id,
              label: `${account.account_id} - ${account.name} - ${account.owner_user_id}`,
            }))}
            isSearchable
            placeholder="Select an account..."
          />
        </div>

        <Row>
          <Col>
            <div className="mb-3">
              <h1 className="mt-1">AI Guidance (UUA)</h1>
              <p className="text-muted">
                User Utterance Assessment configurations provide policy-level guidance to the generator
              </p>
            </div>

            {/* Add/Edit Form */}
            {showForm ? (
              <Card className="ai-guidance-form-card">
                <Card.Body>
                  <div className="ai-guidance-form-header">
                    <h3>{editMode ? 'Edit AI Guidance' : 'Add AI Guidance'}</h3>
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setShowForm(false);
                        setEditMode(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>

                  <Form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="form-section">
                      <h5 className="form-section-title">Basic Information</h5>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="form-label-custom">
                              Title <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.title}
                              onChange={(e) => handleFormChange('title', e.target.value)}
                              placeholder="e.g., topic_cambium-ownership-conflict-instructions"
                              required
                            />
                            <Form.Text className="text-muted">
                              Convention: &lt;assessment_type&gt;-&lt;company_name&gt;-&lt;descriptive-name&gt;
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="form-label-custom">
                              Assessment Type <span className="text-danger">*</span>
                            </Form.Label>
                            <Select
                              value={
                                formData.assessment_type
                                  ? {
                                      value: formData.assessment_type,
                                      label: formData.assessment_type.replace('_', ' ').toUpperCase(),
                                    }
                                  : null
                              }
                              onChange={(option) =>
                                handleFormChange('assessment_type', option?.value || '')
                              }
                              options={[
                                { value: 'topic_', label: 'TOPIC (Match against examples)' },
                                { value: 'characterization_', label: 'CHARACTERIZATION (Score options)' },
                                { value: 'intent_', label: 'INTENT (Match description)' },
                                { value: 'select_', label: 'SELECT (Choose one option)' },
                                { value: 'channel_', label: 'CHANNEL (Always applies)' },
                              ]}
                              placeholder="Select assessment type..."
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="form-label-custom">Workflow Stage</Form.Label>
                            <Select
                              value={{
                                value: formData.workflow_stage,
                                label: formData.workflow_stage === 'pre_generator' ? 'Pre-Generator' : 'Post-Generator',
                              }}
                              onChange={(option) =>
                                handleFormChange('workflow_stage', option?.value || 'pre_generator')
                              }
                              options={[
                                { value: 'pre_generator', label: 'Pre-Generator' },
                                { value: 'post_generator', label: 'Post-Generator' },
                              ]}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="form-label-custom">Model</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.model}
                              onChange={(e) => handleFormChange('model', e.target.value)}
                              placeholder="openai-gpt-4o-mini"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3">
                        <Form.Label className="form-label-custom">System Prompt</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={formData.system_prompt}
                          onChange={(e) => handleFormChange('system_prompt', e.target.value)}
                          placeholder="Leave empty to use default prompt for assessment type"
                        />
                      </Form.Group>
                    </div>

                    {/* Assessment-specific fields */}
                    {formData.assessment_type === 'topic_' && (
                      <div className="form-section">
                        <h5 className="form-section-title">Topic Text Samples</h5>
                        {formData.topic_text_samples.map((sample: string, index: number) => (
                          <div key={index} className="d-flex gap-2 mb-2">
                            <Form.Control
                              type="text"
                              value={sample}
                              onChange={(e) => updateArrayField('topic_text_samples', index, e.target.value)}
                              placeholder="Example user question"
                            />
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeArrayField('topic_text_samples', index)}
                              disabled={formData.topic_text_samples.length === 1}
                            >
                              <i className="fa-solid fa-trash"></i>
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => addArrayField('topic_text_samples')}
                        >
                          <i className="fa-solid fa-plus me-2"></i>
                          Add Sample
                        </Button>
                      </div>
                    )}

                    {formData.assessment_type === 'intent_' && (
                      <div className="form-section">
                        <h5 className="form-section-title">Intent Description</h5>
                        <Form.Group className="mb-3">
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={formData.description}
                            onChange={(e) => handleFormChange('description', e.target.value)}
                            placeholder="Describe what to look for in the user question"
                          />
                        </Form.Group>
                      </div>
                    )}

                    {(formData.assessment_type === 'characterization_' || formData.assessment_type === 'select_') && (
                      <div className="form-section">
                        <h5 className="form-section-title">Options</h5>
                        {formData.options.map((option: string, index: number) => (
                          <div key={index} className="d-flex gap-2 mb-2">
                            <Form.Control
                              type="text"
                              value={option}
                              onChange={(e) => updateArrayField('options', index, e.target.value)}
                              placeholder="Option text or description"
                            />
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeArrayField('options', index)}
                              disabled={formData.options.length === 1}
                            >
                              <i className="fa-solid fa-trash"></i>
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => addArrayField('options')}
                        >
                          <i className="fa-solid fa-plus me-2"></i>
                          Add Option
                        </Button>
                        <Form.Text className="text-muted d-block mt-2">
                          Remember to add an "Other" option to avoid forcing bad choices
                        </Form.Text>
                      </div>
                    )}

                    {/* Workflow Actions */}
                    <div className="form-section">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="form-section-title mb-0">Workflow Actions</h5>
                        <Button variant="primary" size="sm" onClick={addWorkflowAction}>
                          <i className="fa-solid fa-plus me-2"></i>
                          Add Action
                        </Button>
                      </div>

                      {formData.workflow_actions.length === 0 && (
                        <Alert variant="info">
                          No workflow actions defined. Add at least one action to specify what happens when this UUA triggers.
                        </Alert>
                      )}

                      {formData.workflow_actions.map((action: WorkflowAction, index: number) => (
                        <Card key={index} className="mb-3 workflow-action-card">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6>Action {index + 1}</h6>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeWorkflowAction(index)}
                              >
                                <i className="fa-solid fa-trash"></i>
                              </Button>
                            </div>

                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label className="form-label-custom">Action Type</Form.Label>
                                  <Select
                                    value={{
                                      value: action.action_type,
                                      label: action.action_type.replace(/_/g, ' ').toUpperCase(),
                                    }}
                                    onChange={(option) =>
                                      updateWorkflowAction(index, 'action_type', option?.value)
                                    }
                                    options={[
                                      { value: 'prompt_addition', label: 'PROMPT ADDITION' },
                                      { value: 'custom_system_prompt', label: 'CUSTOM SYSTEM PROMPT' },
                                      { value: 'immediate_response', label: 'IMMEDIATE RESPONSE' },
                                      { value: 'retrieval_adjustment', label: 'RETRIEVAL ADJUSTMENT' },
                                      { value: 'answer_adjustment', label: 'ANSWER ADJUSTMENT' },
                                      { value: 'use_retrievals_N', label: 'USE RETRIEVALS N' },
                                      { value: 'tag_utterance_type', label: 'TAG UTTERANCE TYPE' },
                                      { value: 'tag_utterance_characterization', label: 'TAG UTTERANCE CHARACTERIZATION' },
                                      { value: 'mcp', label: 'MCP' },
                                      { value: 'tool', label: 'TOOL' },
                                    ]}
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label className="form-label-custom">Score Threshold</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="0.1"
                                    value={action.trigger_condition.score_threshold || 0.5}
                                    onChange={(e) =>
                                      updateWorkflowAction(index, 'trigger_condition', {
                                        ...action.trigger_condition,
                                        score_threshold: parseFloat(e.target.value),
                                      })
                                    }
                                  />
                                </Form.Group>
                              </Col>
                            </Row>

                            {/* Action-specific fields */}
                            {action.action_type === 'prompt_addition' && (
                              <>
                                <Form.Group className="mb-3">
                                  <Form.Label className="form-label-custom">Prompt to Add</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={action.prompt_addition?.prompt_to_add || ''}
                                    onChange={(e) =>
                                      updateWorkflowAction(index, 'prompt_addition', {
                                        ...action.prompt_addition,
                                        prompt_to_add: e.target.value,
                                      })
                                    }
                                    placeholder="Enter the prompt text to add to the system prompt"
                                  />
                                </Form.Group>
                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label className="form-label-custom">Priority</Form.Label>
                                      <Form.Control
                                        type="number"
                                        value={action.prompt_addition?.priority || 1}
                                        onChange={(e) =>
                                          updateWorkflowAction(index, 'prompt_addition', {
                                            ...action.prompt_addition,
                                            priority: parseInt(e.target.value),
                                          })
                                        }
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label className="form-label-custom">&nbsp;</Form.Label>
                                      <Form.Check
                                        type="checkbox"
                                        label="Include if lower priority"
                                        checked={action.prompt_addition?.include_if_lower_priority_p ?? true}
                                        onChange={(e) =>
                                          updateWorkflowAction(index, 'prompt_addition', {
                                            ...action.prompt_addition,
                                            include_if_lower_priority_p: e.target.checked,
                                          })
                                        }
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </>
                            )}

                            {action.action_type === 'mcp' && (
                              <>
                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label className="form-label-custom">Server URL</Form.Label>
                                      <Form.Control
                                        type="text"
                                        value={action.mcp?.server_url || ''}
                                        onChange={(e) =>
                                          updateWorkflowAction(index, 'mcp', {
                                            ...action.mcp,
                                            type: 'mcp',
                                            server_url: e.target.value,
                                          })
                                        }
                                        placeholder="https://mcp.ept.ai/mcp?company_id=133"
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label className="form-label-custom">Server Label</Form.Label>
                                      <Form.Control
                                        type="text"
                                        value={action.mcp?.server_label || ''}
                                        onChange={(e) =>
                                          updateWorkflowAction(index, 'mcp', {
                                            ...action.mcp,
                                            server_label: e.target.value,
                                          })
                                        }
                                        placeholder="EptAI-MCP"
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                  <Form.Label className="form-label-custom">Server Description</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={action.mcp?.server_description || ''}
                                    onChange={(e) =>
                                      updateWorkflowAction(index, 'mcp', {
                                        ...action.mcp,
                                        server_description: e.target.value,
                                      })
                                    }
                                    placeholder="MCP server to find products in the semiconductor industry"
                                  />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                  <Form.Label className="form-label-custom">Require Approval</Form.Label>
                                  <Select
                                    value={{
                                      value: action.mcp?.require_approval || 'never',
                                      label: action.mcp?.require_approval || 'never',
                                    }}
                                    onChange={(option) =>
                                      updateWorkflowAction(index, 'mcp', {
                                        ...action.mcp,
                                        require_approval: option?.value || 'never',
                                      })
                                    }
                                    options={[
                                      { value: 'never', label: 'never' },
                                      { value: 'always', label: 'always' },
                                      { value: 'first_time', label: 'first_time' },
                                    ]}
                                  />
                                </Form.Group>
                              </>
                            )}

                            {action.action_type === 'use_retrievals_N' && (
                              <Form.Group className="mb-3">
                                <Form.Label className="form-label-custom">Number of Retrievals</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={action.use_retrievals_N || 0}
                                  onChange={(e) =>
                                    updateWorkflowAction(index, 'use_retrievals_N', parseInt(e.target.value))
                                  }
                                  placeholder="Number of retrievals to use"
                                />
                              </Form.Group>
                            )}

                            {(action.action_type === 'custom_system_prompt' ||
                              action.action_type === 'immediate_response' ||
                              action.action_type === 'retrieval_adjustment_prompt' ||
                              action.action_type === 'answer_adjustment_prompt') && (
                              <Form.Group className="mb-3">
                                <Form.Label className="form-label-custom">
                                  {action.action_type === 'custom_system_prompt' && 'Custom System Prompt'}
                                  {action.action_type === 'immediate_response' && 'Immediate Response Text'}
                                  {action.action_type === 'retrieval_adjustment_prompt' && 'Retrieval Adjustment Prompt'}
                                  {action.action_type === 'answer_adjustment_prompt' && 'Answer Adjustment Prompt'}
                                </Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={4}
                                  value={
                                    action.action_type === 'custom_system_prompt'
                                      ? action.custom_system_prompt || ''
                                      : action.action_type === 'immediate_response'
                                      ? action.immediate_response || ''
                                      : action.action_type === 'retrieval_adjustment_prompt'
                                      ? action.retrieval_adjustment_prompt || ''
                                      : action.answer_adjustment_prompt || ''
                                  }
                                  onChange={(e) => {
                                    const field =
                                      action.action_type === 'custom_system_prompt'
                                        ? 'custom_system_prompt'
                                        : action.action_type === 'immediate_response'
                                        ? 'immediate_response'
                                        : action.action_type === 'retrieval_adjustment_prompt'
                                        ? 'retrieval_adjustment_prompt'
                                        : 'answer_adjustment_prompt';
                                    updateWorkflowAction(index, field, e.target.value);
                                  }}
                                  placeholder="Enter the text"
                                />
                              </Form.Group>
                            )}

                            {(action.action_type === 'tag_utterance_type' ||
                              action.action_type === 'tag_utterance_characterization') && (
                              <Form.Group className="mb-3">
                                <Form.Label className="form-label-custom">Tag Value</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={action.tag_value || ''}
                                  onChange={(e) => updateWorkflowAction(index, 'tag_value', e.target.value)}
                                  placeholder="Tag value to add to response"
                                />
                              </Form.Group>
                            )}
                          </Card.Body>
                        </Card>
                      ))}
                    </div>

                    {/* Channel Selection */}
                    <div className="form-section">
                      <h5 className="form-section-title">Channels (Optional)</h5>
                      <Form.Group className="mb-3">
                        <Select
                          isMulti
                          value={selectedChannels}
                          onChange={(selected) => setSelectedChannels(selected as any)}
                          options={channels}
                          placeholder="Select channels (leave empty for all channels)"
                          isLoading={loadingChannels}
                        />
                        <Form.Text className="text-muted">
                          If no channels are selected, this UUA will apply to all channels in the account
                        </Form.Text>
                      </Form.Group>
                    </div>

                    <div className="d-flex gap-2 justify-content-end pt-3 border-top">
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setShowForm(false);
                          setEditMode(false);
                          resetForm();
                        }}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                      <Button variant="primary" type="submit" disabled={submitting}>
                        {submitting ? 'Saving...' : editMode ? 'Update' : 'Create'}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            ) : (
              <>
                {/* Add Button */}
                <Card className="ai-guidance-add-card mb-4">
                  <Card.Body className="text-center py-4">
                    <h5 className="mb-3">AI Guidance Management</h5>
                    <p className="text-muted mb-3">
                      Create and manage User Utterance Assessment configurations
                    </p>
                    <Button variant="primary" onClick={() => setShowForm(true)}>
                      <i className="fa-solid fa-plus me-2"></i>
                      Add AI Guidance
                    </Button>
                  </Card.Body>
                </Card>

                {/* UUA List */}
                <Card className="ai-guidance-list-card">
                  <Card.Header className="ai-guidance-list-header">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h5 className="mb-0">Existing AI Guidance</h5>
                        <small className="text-muted">{uuaList.length} configuration(s)</small>
                      </div>
                      {loadingUuas && (
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {uuaList.length === 0 ? (
                      <Alert variant="info">
                        <i className="fa-solid fa-info-circle me-2"></i>
                        No AI Guidance configurations found for this account. Click "Add AI Guidance" to create one.
                      </Alert>
                    ) : (
                      <Accordion>
                        {uuaList.map((uua, index) => (
                          <Accordion.Item key={uua.id} eventKey={index.toString()}>
                            <Accordion.Header>
                              <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                                <div>
                                  <strong>{uua.title}</strong>
                                  <div className="text-muted small">
                                    <Badge bg="secondary" className="me-2">
                                      {getAssessmentType(uua.title).replace('_', '').toUpperCase() || 'UNKNOWN'}
                                    </Badge>
                                    {uua.channels.length > 0 && (
                                      <span className="me-2">
                                        <i className="fa-solid fa-hashtag me-1"></i>
                                        {uua.channels.length} channel(s)
                                      </span>
                                    )}
                                    <span>
                                      <i className="fa-solid fa-bolt me-1"></i>
                                      {uua.content.workflow_actions.length} action(s)
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Accordion.Header>
                            <Accordion.Body>
                              <div className="uua-details">
                                <div className="mb-3">
                                  <strong>Assessment Stage:</strong>{' '}
                                  <Badge bg="info">
                                    {uua.content.assessment_spec.workflow_stage || 'pre_generator'}
                                  </Badge>
                                </div>

                                <div className="mb-3">
                                  <strong>Workflow Actions:</strong>
                                  <ul className="mt-2">
                                    {uua.content.workflow_actions.map((action, idx) => (
                                      <li key={idx}>
                                        <Badge bg="primary" className="me-2">
                                          {action.action_type}
                                        </Badge>
                                        Threshold: {action.trigger_condition.score_threshold || 'N/A'}
                                      </li>
                                    ))}
                                  </ul>
                                </div>

                                {uua.channels.length > 0 && (
                                  <div className="mb-3">
                                    <strong>Channels:</strong> {uua.channels.join(', ')}
                                  </div>
                                )}

                                <div className="d-flex gap-2 mt-3">
                                  <Button variant="outline-primary" size="sm" onClick={() => handleView(uua)}>
                                    <i className="fa-solid fa-eye me-2"></i>
                                    View Details
                                  </Button>
                                  <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(uua)}>
                                    <i className="fa-solid fa-edit me-2"></i>
                                    Edit
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                      setUuaToDelete(uua);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    <i className="fa-solid fa-trash me-2"></i>
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    )}
                  </Card.Body>
                </Card>
              </>
            )}
          </Col>
        </Row>

        {/* View Modal */}
        <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>AI Guidance Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedUua && (
              <div>
                <h5>{selectedUua.title}</h5>
                <hr />
                <pre className="bg-light p-3 rounded" style={{ maxHeight: '500px', overflow: 'auto' }}>
                  {JSON.stringify(selectedUua.content, null, 2)}
                </pre>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowViewModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to delete this AI Guidance configuration?</p>
            {uuaToDelete && (
              <Alert variant="warning">
                <strong>{uuaToDelete.title}</strong>
              </Alert>
            )}
            <p className="text-muted">This action cannot be undone.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
      <ToastContainer />
    </SweetLayout>
  );
};

export default AiGuidance;

